import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Select,
  FormControl,
  InputLabel,
  alpha,
  useTheme,
  Stack,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  MoreVert,
  Visibility,
  Search,
  ConfirmationNumberOutlined,
  EmailOutlined,
  AccessTime,
  Update,
  FilterList,
} from '@mui/icons-material';
import { apiService } from '../services/api.service';
import { Ticket, TicketStatus } from '../types';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';

const Tickets: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any = { page: page + 1, limit: rowsPerPage, search };
      if (statusFilter) filters.status = statusFilter;
      const result = await apiService.getTickets(filters);
      setTickets(result.data);
      setTotal(result.pagination.total);
    } catch (error) {
      console.error('Failed to load tickets:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, search]);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, ticket: Ticket) => {
    setAnchorEl(event.currentTarget);
    setSelectedTicket(ticket);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleViewDetails = () => {
    if (selectedTicket) navigate(`/tickets/${selectedTicket.id}`);
    handleMenuClose();
  };

  const getStatusStyles = (status: TicketStatus) => {
    switch (status) {
      case 'open': return { color: 'warning.main', label: 'Open', bg: alpha(theme.palette.warning.main, 0.15), border: alpha(theme.palette.warning.main, 0.2) };
      case 'inProgress': return { color: 'info.main', label: 'In Progress', bg: alpha(theme.palette.info.main, 0.15), border: alpha(theme.palette.info.main, 0.2) };
      case 'resolved': return { color: 'success.main', label: 'Resolved', bg: alpha(theme.palette.success.main, 0.15), border: alpha(theme.palette.success.main, 0.2) };
      case 'closed': return { color: 'text.secondary', label: 'Closed', bg: alpha(theme.palette.grey[500], 0.15), border: alpha(theme.palette.grey[500], 0.2) };
      default: return { color: 'text.secondary', label: status, bg: alpha(theme.palette.grey[500], 0.15), border: alpha(theme.palette.grey[500], 0.2) };
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Layout>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{
          mb: 4,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2
        }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, letterSpacing: -1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>Support Center</Typography>
            <Typography color="text.secondary" variant="body2">Manage user support tickets and resolve platform issues.</Typography>
          </Box>
        </Box>
        <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: theme.shadows[2] }}>
          <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderBottom: 1, borderColor: 'divider', bgcolor: theme.palette.mode === 'light' ? 'grey.50' : alpha(theme.palette.common.white, 0.02) }}>
            <TextField
              sx={{ flex: { xs: '1 1 100%', sm: 1 }, minWidth: { xs: '100%', sm: 300 }, '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: theme.palette.mode === 'light' ? 'grey.50' : alpha(theme.palette.common.white, 0.05) } }}
              placeholder="Search tickets..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} /> }}
              size="small"
            />

            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 }, flex: { xs: '1 1 100%', sm: 'auto' } }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value as any); setPage(0); }}
                label="Status"
                sx={{ borderRadius: 3, bgcolor: theme.palette.mode === 'light' ? 'background.paper' : alpha(theme.palette.common.white, 0.05) }}
                IconComponent={FilterList}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="inProgress">In Progress</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress size={32} thickness={5} /></Box>
          ) : (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 1100 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : alpha(theme.palette.common.white, 0.04) }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>Identification</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>Inquiry Details</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>User Information</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>Current Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>Timelines</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tickets.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                        <Typography variant="body1" color="text.secondary" fontWeight={600}>No support tickets match your criteria</Typography>
                      </TableCell></TableRow>
                    ) : (
                      tickets.map((ticket) => {
                        const status = getStatusStyles(ticket.status);
                        return (
                          <TableRow key={ticket.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <ConfirmationNumberOutlined fontSize="small" color="disabled" />
                                <Typography variant="body2" sx={{ fontWeight: 800, fontFamily: 'monospace' }}>#{ticket.ticketNumber}</Typography>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 300 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>{ticket.subject}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4, opacity: 0.8 }}>
                                {ticket.description.length > 80 ? `${ticket.description.substring(0, 80)}...` : ticket.description}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', fontSize: '0.875rem', fontWeight: 700 }}>{ticket.userName.charAt(0)}</Avatar>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{ticket.userName}</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><EmailOutlined sx={{ fontSize: 12 }} /> {ticket.userEmail}</Typography>
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip label={status.label} size="small"
                                sx={{
                                  fontWeight: 800,
                                  fontSize: '0.625rem',
                                  textTransform: 'uppercase',
                                  bgcolor: status.bg,
                                  color: status.color,
                                  borderRadius: 1.5,
                                  border: '1px solid',
                                  borderColor: status.border,
                                  height: 24
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><AccessTime sx={{ fontSize: 12 }} /> <strong>Created:</strong> {formatDate(ticket.createdAt)}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Update sx={{ fontSize: 12 }} /> <strong>Updated:</strong> {formatDate(ticket.updatedAt)}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="View Ticket Details">
                                <IconButton onClick={(e) => handleMenuClick(e, ticket)} size="small"><MoreVert fontSize="small" /></IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, n) => setPage(n)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                sx={{ borderTop: 1, borderColor: 'divider' }}
              />
            </>
          )}
        </Paper>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} PaperProps={{ sx: { borderRadius: 3, minWidth: 180, mt: 1 } }}>
          <MenuItem onClick={handleViewDetails} sx={{ gap: 1.5, py: 1.25 }}>
            <Visibility fontSize="small" color="action" /> <Typography variant="body2" fontWeight={600}>View Conversation</Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Layout>
  );
};

export default Tickets;
